import React, { useEffect, useRef, useState } from "react";
import "./Landing.scss";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFacebookF, faInstagram } from "@fortawesome/free-brands-svg-icons";
import {
  faLocationDot,
  faEnvelope,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import aboutVisual from "../../assets/LandingPage/one-region-two-horizons.jpg";
import aftLogoFull from "../../assets/other/aft-logo-full.png";
import experienceAward from "../../assets/LandingPage/experience-award.jpg";
import recapPoster from "../../assets/LandingPage/recap-poster.jpg";
import sponsorImedi from "../../assets/Sponsors/sponsor-imedi.png";
import sponsorAcharaNews from "../../assets/Sponsors/sponsor-achara-news.png";
import sponsorRadioAchara from "../../assets/Sponsors/sponsor-radio-achara.png";
import sponsorTvAchara from "../../assets/Sponsors/sponsor-tv-achara.png";
import sponsorMinistry from "../../assets/Sponsors/sponsor-ministry-culture.png";
import sponsorShuakhevi from "../../assets/Sponsors/sponsor-shuakhevi.png";
import sponsorShuakheviCultureCenter from "../../assets/Sponsors/sponsor-shuakhevi-culture-center.jpg";
import sponsorTbilisiSunrise from "../../assets/Sponsors/sponsor-tbilisi-sunrise.jpg";
import { useLanguage } from "../../i18n/LanguageContext";
import { contact, footer, landing } from "../../i18n/content";
import { fetchGalleryYears } from "../../lib/galleryApi";
import { urlForImage } from "../../lib/sanityClient";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Round-robins photos across every gallery year (so a year with fewer
// photos still gets represented, not just crowded out by a bigger one),
// then shuffles the picked set so the marquee doesn't play in year blocks.
function buildReelPool(years, targetCount) {
  const perYear = (years || []).map((y) => ({
    year: y.year,
    photos: shuffle(y.photos || []),
  }));
  const pool = [];
  let round = 0;
  let addedThisRound = true;
  while (addedThisRound && pool.length < targetCount) {
    addedThisRound = false;
    for (const y of perYear) {
      if (round < y.photos.length) {
        pool.push({ year: y.year, img: urlForImage(y.photos[round], { width: 700, height: 1244 }) });
        addedThisRound = true;
        if (pool.length >= targetCount) break;
      }
    }
    round++;
  }
  return shuffle(pool);
}

const REEL_TARGET_COUNT = 18;

const sponsors = [
  { img: sponsorImedi, name: "TV Imedi" },
  { img: sponsorAcharaNews, name: "Achara Newspaper" },
  { img: sponsorRadioAchara, name: "Radio Achara 104.5 FM" },
  { img: sponsorTvAchara, name: "TV Achara" },
  { img: sponsorMinistry, name: "Ministry of Culture of the Autonomous Republic of Ajara" },
  { img: sponsorShuakhevi, name: "Shuakhevi Municipality" },
  { img: sponsorShuakheviCultureCenter, name: "Shuakhevi Culture Center" },
  { img: sponsorTbilisiSunrise, name: "Film Festival Tbilisi Sunrise" },
];

// Repeated so a single marquee track is wide enough to fill even
// ultra-wide monitors on first paint, instead of leaving blank space
// until the loop catches up.
const sponsorsLoop = Array.from({ length: 7 }, () => sponsors).flat();

// Temporarily disabled, not deleted — flip back to true to restore.
const SHOW_HERO_RIDGE = false;
const SHOW_RECAP_SECTION = false;

const Landing = () => {
  const { t } = useLanguage();
  const heroVideoRef = useRef(null);
  const [reelPool, setReelPool] = useState([]);
  const reelClips = reelPool.map((item, i) => ({
    img: item.img,
    year: item.year,
    tag: t(landing.reelTags[i % landing.reelTags.length]),
  }));

  useEffect(() => {
    let active = true;
    fetchGalleryYears()
      .then((years) => {
        if (active) setReelPool(buildReelPool(years, REEL_TARGET_COUNT));
      })
      .catch(() => {
        if (active) setReelPool([]);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const video = heroVideoRef.current;
    if (!video) return;
    // React doesn't reflect the `muted` prop as an HTML attribute (only
    // the JS property), which blocks autoplay on stricter browsers
    // (notably mobile Safari). Set both explicitly.
    video.muted = true;
    video.setAttribute("muted", "");
    video.play().catch(() => {});
  }, []);

  return (
    <div className="landing_test_wrapper">
      <header className="lw-hero">
        <div className="lw-hero-media">
          <div className="lw-hero-video-bg" aria-hidden="true">
            <video
              ref={heroVideoRef}
              autoPlay
              muted
              loop
              playsInline
              poster={recapPoster}
            >
              <source src="/videos/recap-2024.mp4" type="video/mp4" />
            </video>
            <div className="lw-hero-scrim" />
          </div>

          {SHOW_HERO_RIDGE && (
            <div className="lw-ridge" aria-hidden="true">
              <svg viewBox="0 0 1180 200" preserveAspectRatio="none">
                <polygon
                  points="0,200 0,110 90,50 180,120 260,70 340,115 430,60 520,125 620,80 720,130 820,75 920,120 1020,85 1100,118 1180,95 1180,200"
                  fill="#2c352f"
                  opacity="0.92"
                />
                <polygon
                  points="0,200 0,150 120,130 240,160 360,125 480,155 600,120 720,158 840,130 960,160 1080,135 1180,155 1180,200"
                  fill="#182019"
                />
              </svg>
            </div>
          )}

          <div className="lw-wrap lw-hero-copy">
            <div className="lw-eyebrow">
              <span className="lw-dot" />
              {t(landing.eyebrow)}
            </div>
            <h1 className="lw-title">
              {t(landing.titleLine1)}
              <br />
              {t(landing.titleLine2)}
            </h1>
            <p className="lw-lede">{t(landing.lede)}</p>
            <div className="lw-cta-row">
              <Link className="lw-btn" to="/submission">
                {t(landing.ctaSubmit)}
              </Link>
              <a className="lw-btn-ghost" href="#lw-history">
                {t(landing.ctaStory)}
              </a>
            </div>
          </div>
        </div>

        <div className="lw-reel-tray" aria-label="Highlights from past festivals">
          <div className="lw-reel-marquee">
            <div className="lw-reel-track">
              {reelClips.map((clip, i) => (
                <div className="lw-reel-card" key={i}>
                  <img src={clip.img} alt="" />
                  <div className="lw-reel-overlay" />
                  <div className="lw-rec">
                    <span className="lw-rec-dot" />
                    REC
                  </div>
                  <div className="lw-tag">
                    <span>{clip.tag}</span>
                    <span>{clip.year}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="lw-reel-track" aria-hidden="true">
              {reelClips.map((clip, i) => (
                <div className="lw-reel-card" key={`dup-${i}`}>
                  <img src={clip.img} alt="" />
                  <div className="lw-reel-overlay" />
                  <div className="lw-rec">
                    <span className="lw-rec-dot" />
                    REC
                  </div>
                  <div className="lw-tag">
                    <span>{clip.tag}</span>
                    <span>{clip.year}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </header>

      <div className="lw-sponsors" aria-label="Festival sponsors and partners">
        <div className="lw-sponsors-marquee">
          <div className="lw-sponsors-track">
            {sponsorsLoop.map((s, i) => (
              <div className="lw-sponsor-logo" key={i}>
                <img src={s.img} alt={s.name} />
              </div>
            ))}
          </div>
          <div className="lw-sponsors-track" aria-hidden="true">
            {sponsorsLoop.map((s, i) => (
              <div className="lw-sponsor-logo" key={`dup-${i}`}>
                <img src={s.img} alt="" />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="lw-statbar">
        <div className="lw-wrap">
          <div className="lw-stat">
            <b>{t(landing.statAgeValue)}</b>{t(landing.statAge)}
          </div>
          <div className="lw-stat">
            <b>{t(landing.statCountriesValue)}</b>{t(landing.statCountries)}
          </div>
          <div className="lw-stat">
            <b>{t(landing.statFilmsValue)}</b>{t(landing.statFilms)}
          </div>
          <div className="lw-stat">
            <b>{t(landing.statLengthValue)}</b>{t(landing.statLength)}
          </div>
          <div className="lw-stat">
            <b>{t(landing.estBadge)}</b>{t(landing.statEst)}
          </div>
        </div>
      </div>

      <section className="lw-about">
        <div className="lw-wrap lw-frame-grid">
          <div>
            <span className="lw-kicker">{t(landing.regionKicker)}</span>
            <h2 className="lw-h-title">{t(landing.regionTitle)}</h2>
            <p>{t(landing.regionP1)}</p>
            <p>{t(landing.regionP2)}</p>
          </div>
          <div className="lw-about-visual lw-about-visual--landscape">
            <img src={aboutVisual} alt="Scene from a past Adjarian Film Tales festival" />
            <div className="lw-reel-overlay" />
            <div className="lw-rec">
              <span className="lw-rec-dot" />
              REC
            </div>
            <div className="lw-tag">
              <span>{t(landing.regionTag)}</span>
              <span>4:3</span>
            </div>
          </div>
        </div>
      </section>

      <section className="lw-submit">
        <div className="lw-wrap">
          <div className="lw-section-head">
            <span className="lw-kicker">{t(landing.submitKicker)}</span>
            <h2 className="lw-h-title">{t(landing.submitTitle)}</h2>
          </div>
          <div className="lw-steps">
            <div className="lw-step">
              <span className="lw-badge">01</span>
              <div>
                <h3>{t(landing.step1Title)}</h3>
                <p>{t(landing.step1Body)}</p>
              </div>
            </div>
            <div className="lw-step">
              <span className="lw-badge">02</span>
              <div>
                <h3>{t(landing.step2Title)}</h3>
                <p>{t(landing.step2Body)}</p>
              </div>
            </div>
            <div className="lw-step">
              <span className="lw-badge">03</span>
              <div>
                <h3>{t(landing.step3Title)}</h3>
                <p>
                  {t(landing.step3BodyPre)}{" "}
                  <a href="mailto:cinephoneadjaraff@gmail.com">
                    cinephoneadjaraff@gmail.com
                  </a>
                  . {t(landing.step3BodyPost)}
                </p>
              </div>
            </div>
          </div>
          <Link className="lw-btn-ghost lw-submit-link" to="/submission">
            {t(landing.submitLink)}
          </Link>
        </div>
      </section>

      {SHOW_RECAP_SECTION && (
        <section className="lw-recap">
          <div className="lw-wrap">
            <div className="lw-section-head">
              <span className="lw-kicker">{t(landing.recapKicker)}</span>
              <h2 className="lw-h-title">{t(landing.recapTitle)}</h2>
            </div>
            <div className="lw-recap-player">
              <video controls preload="metadata" poster={recapPoster} playsInline>
                <source src="/videos/recap-2024.mp4" type="video/mp4" />
                {t(landing.recapFallback)}{" "}
                <a href="/videos/recap-2024.mp4">{t(landing.recapFallbackLink)}</a>{" "}
                {t(landing.recapFallbackEnd)}
              </video>
            </div>
            <p className="lw-recap-caption">{t(landing.recapCaption)}</p>
          </div>
        </section>
      )}

      <section className="lw-history" id="lw-history">
        <div className="lw-wrap lw-history-grid">
          <div className="lw-history-copy">
            <div className="lw-section-head">
              <span className="lw-kicker">{t(landing.historyKicker)}</span>
              <h2 className="lw-h-title">{t(landing.historyTitle)}</h2>
            </div>
            <div className="lw-timeline">
              <div className="lw-tl-row">
                <span className="lw-tl-year">2022</span>
                <p>{t(landing.y2022)}</p>
              </div>
              <div className="lw-tl-row">
                <span className="lw-tl-year">2023</span>
                <p>{t(landing.y2023)}</p>
              </div>
              <div className="lw-tl-row">
                <span className="lw-tl-year">2025</span>
                <p>{t(landing.y2025)}</p>
              </div>
              <div className="lw-tl-row lw-upcoming">
                <span className="lw-tl-year">2026</span>
                <p>{t(landing.y2026)}</p>
              </div>
            </div>
            <Link className="lw-btn-ghost lw-submit-link" to="/about_us#lw-history">
              {t(landing.historyLink)}
            </Link>
          </div>
          <div className="lw-history-logo">
            <img src={aftLogoFull} alt="Adjarian Film Tales" />
          </div>
        </div>
      </section>

      <section className="lw-about lw-experience">
        <div className="lw-wrap lw-frame-grid">
          <div>
            <div className="lw-section-head">
              <span className="lw-kicker">{t(landing.experienceKicker)}</span>
              <h2 className="lw-h-title">{t(landing.experienceTitle)}</h2>
            </div>
            <p>{t(landing.experienceP1)}</p>
            <p>{t(landing.experienceP2)}</p>
            <p>{t(landing.experienceP3)}</p>
            <p>{t(landing.experienceP4)}</p>
          </div>
          <div className="lw-about-visual lw-about-visual--landscape">
            <img src={experienceAward} alt="Winner at the Adjarian Film Tales awards ceremony" />
            <div className="lw-reel-overlay" />
            <div className="lw-rec">
              <span className="lw-rec-dot" />
              REC
            </div>
            <div className="lw-tag">
              <span>{t(landing.experienceTag)}</span>
              <span>4:3</span>
            </div>
          </div>
        </div>
      </section>

      <div className="lw-sponsors" aria-label="Festival sponsors and partners">
        <div className="lw-sponsors-marquee">
          <div className="lw-sponsors-track">
            {sponsorsLoop.map((s, i) => (
              <div className="lw-sponsor-logo" key={i}>
                <img src={s.img} alt={s.name} />
              </div>
            ))}
          </div>
          <div className="lw-sponsors-track" aria-hidden="true">
            {sponsorsLoop.map((s, i) => (
              <div className="lw-sponsor-logo" key={`dup-${i}`}>
                <img src={s.img} alt="" />
              </div>
            ))}
          </div>
        </div>
      </div>

      <footer className="lw-footer">
        <div className="lw-wrap lw-footer-grid">
          <div>
            <h4>Adjarian Film Tales</h4>
            <p>
              <FontAwesomeIcon icon={faLocationDot} /> {t(contact.address)}
            </p>
          </div>
          <div>
            <h4>{t(landing.footerReachUs)}</h4>
            <p>
              <FontAwesomeIcon icon={faEnvelope} />{" "}
              <a href="mailto:cinephoneadjaraff@gmail.com">
                cinephoneadjaraff@gmail.com
              </a>
            </p>
            <p>
              <FontAwesomeIcon icon={faPhone} />{" "}
              <a href="tel:+995597780501">+995 597 780 501</a>
              <br />
              <a href="tel:+995591712141">+995 591 712 141</a>
            </p>
          </div>
          <div>
            <h4>{t(contact.follow)}</h4>
            <p>
              <a
                href="https://www.facebook.com/adjarianfilmtales"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faFacebookF} /> Facebook
              </a>
            </p>
            <p>
              <a
                href="https://www.instagram.com/adjarianfilmtales/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faInstagram} /> Instagram
              </a>
            </p>
          </div>
        </div>
        <div className="lw-wrap lw-foot-bottom">
          <span>{t(footer.est)}</span>
          <Link to="/contact">{t(landing.footerFullContact)}</Link>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
